import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { KINDS, PUNE_AREAS, flowFor, itemsFor } from './questions';
import { G } from './App';

// One question per screen. Answers go straight into the broker's Janus
// leads, no account needed; WhatsApp is there if sending fails.
const BROKER_WHATSAPP = '917770060901';
const SITE_SLUG = 'brokerinasuit';
const ENDPOINT = 'https://gyxndffnrndysrtfaekh.supabase.co/functions/v1/site-form';
// Publishable key: safe in a public page.
const PUBLIC_KEY = 'sb_publishable_U2cepVDZrVbwxJRGwTR2jg_cZ8HXqm5';

// Styled as a tailor's order ticket: "Real estate tailored for you".
const PAPER = '#ECE5D6';
const INK = '#1C1A16';
const ACCENT = '#9E3A2B'; // tailor's chalk red, for ticks and the stamp
const TEXT = INK;
const MUTED = 'rgba(28,26,22,0.58)';
const LINE = 'rgba(28,26,22,0.2)';
const MONO = "'DM Mono', ui-monospace, Menlo, monospace";
const EASE = [0.22, 1, 0.36, 1];
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Broker details stay after sending, so the next client is quicker.
const KEEP = { client: [], broker: ['broker', 'brokerPhone'] };
const storeKey = (form) => `bias-requirement-${form}`;

function load(form) {
  try { return JSON.parse(localStorage.getItem(storeKey(form)) || '{}') || {}; } catch { return {}; }
}

const filled = (v) => (Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.trim() !== '');
const shown = (item, s) => !!item && (!item.when || item.when(s));
const digits = (v) => String(v || '').replace(/\D/g, '');
const areasOf = (s) => [...(s.areaPicks || []), ...(s.areas || '').split(',').map(x => x.trim()).filter(Boolean)].join(', ');
const isTouch = () => window.matchMedia?.('(pointer: coarse)').matches;

function answerLines(items, s) {
  const lines = [['Looking for', KINDS[s.kind] || '']];
  const skip = new Set(['kind', 'name', 'phone', 'broker', 'brokerPhone']);
  const flow = flowFor('broker', s.kind, true).flat();
  for (const id of flow) {
    const it = items[id];
    if (!it || skip.has(id) || !shown(it, s)) continue;
    let value = '';
    if (it.type === 'pair' || it.type === 'budget') {
      const [a, b] = it.fields;
      value = [filled(s[a.id]) && `${a.label} ${s[a.id].trim()}`, filled(s[b.id]) && `${b.label} ${s[b.id].trim()}`].filter(Boolean).join(', ');
    } else if (it.type === 'places') {
      value = areasOf(s);
    } else if (filled(s[id])) {
      value = Array.isArray(s[id]) ? s[id].join(', ') : s[id].trim();
    }
    if (value) lines.push([it.q, value]);
  }
  return lines;
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function Tick() {
  return (
    <svg width="22" height="20" viewBox="0 0 22 20" style={{ position: 'absolute', left: -2, top: -5, overflow: 'visible' }} aria-hidden="true">
      <motion.path d="M2 11 C4.5 12.5 6.5 14.5 8 17 C10.5 10.5 14.5 5 20.5 1.5" fill="none" stroke={ACCENT} strokeWidth="2.3" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.28, ease: 'easeOut' }}/>
    </svg>
  );
}

function Option({ letter, label, on, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      onMouseEnter={() => !isTouch() && setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16, width: '100%', textAlign: 'left',
        background: hover && !on ? 'rgba(28,26,22,0.035)' : 'transparent',
        border: 'none', borderBottom: `1px solid ${LINE}`, borderRadius: 0,
        color: INK, padding: '15px 4px', fontSize: 17, cursor: 'pointer', transition: 'background 0.15s',
      }}>
      <span style={{ position: 'relative', flex: 'none', width: 18, height: 18, border: `1.5px solid ${INK}` }}>
        {on && <Tick/>}
      </span>
      <span style={{ flex: 1, lineHeight: 1.35 }}>{label}</span>
      {letter && <span className="ok-hint" style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{letter}</span>}
    </button>
  );
}

function BigInput({ value, onChange, placeholder, type = 'text', area, autoFocus, onEnter, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (autoFocus && !isTouch()) ref.current?.focus({ preventScroll: true });
  }, [autoFocus]);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEnter?.(); }
  };
  const style = {
    width: '100%', background: 'transparent', border: 'none', outline: 'none', borderRadius: 0,
    borderBottom: `1px solid ${LINE}`, color: INK, fontSize: 'clamp(18px, 3.6vw, 22px)', fontWeight: 400,
    padding: '10px 0 12px', resize: 'none', fontFamily: MONO, lineHeight: 1.45,
  };
  const props = {
    ref, value: value || '', placeholder, onKeyDown, style, 'aria-label': label,
    onChange: e => onChange(e.target.value),
    onFocus: e => { e.target.style.borderBottomColor = INK; },
    onBlur: e => { e.target.style.borderBottomColor = LINE; },
  };
  if (area) return <textarea rows={2} {...props}/>;
  return <input type={type} inputMode={type === 'tel' ? 'tel' : undefined} autoComplete={type === 'tel' ? 'tel' : type === 'name' ? 'name' : 'off'} {...props}/>;
}

function OkButton({ children = 'OK', onClick, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 30 }}>
      <button type="button" onClick={onClick} disabled={disabled} style={{
        display: 'inline-flex', alignItems: 'center', gap: 12, background: INK, color: PAPER,
        border: 'none', borderRadius: 0, padding: '14px 22px', fontSize: 15, fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
      }}>{children} {!disabled && <ArrowRight size={16}/>}</button>
      <span className="ok-hint" style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>or press Enter</span>
    </div>
  );
}

function TextLink({ children, onClick, color = MUTED }) {
  return (
    <button type="button" onClick={onClick} style={{
      background: 'none', border: 'none', padding: 0, color, fontSize: 14,
      textDecoration: 'underline', textUnderlineOffset: 4, cursor: 'pointer',
    }}>{children}</button>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function RequirementPage({ form }) {
  const isBroker = form === 'broker';
  const [s, setS] = useState(() => ({ kind: 'home', ...load(form) }));
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | whatsapp
  const [error, setError] = useState('');
  const [exact, setExact] = useState(false);
  const [trap, setTrap] = useState('');
  const [sentBy, setSentBy] = useState('');
  // A ticket number, so the client and broker can refer to this brief.
  const [ref, setRef] = useState(() => String(1000 + Math.floor(Math.random() * 9000)));
  const [sentRef, setSentRef] = useState('');
  const started = useRef(Date.now());
  const advancing = useRef(null);

  useEffect(() => { document.title = `${isBroker ? 'Share a client requirement' : 'Your property brief'} · Broker in a Suit`; }, [isBroker]);
  useEffect(() => { try { localStorage.setItem(storeKey(form), JSON.stringify(s)); } catch {} }, [form, s]);

  const items = useMemo(() => itemsFor(form), [form]);
  const screens = useMemo(
    () => flowFor(form, s.kind, s._detail).filter(x => Array.isArray(x) || x === 'gate' || x === 'send' || shown(items[x], s)),
    [form, s, items],
  );
  const at = Math.min(idx, screens.length - 1);
  const screen = screens[at];
  const questionNumber = screens.slice(0, at + 1).filter(x => x !== 'gate' && x !== 'send').length;

  const set = (id, v) => setS(prev => ({ ...prev, [id]: v }));
  const setMany = (patch) => setS(prev => ({ ...prev, ...patch }));

  const go = useCallback((to) => {
    clearTimeout(advancing.current);
    setError('');
    setExact(false);
    setDir(to > at ? 1 : -1);
    setIdx(Math.max(0, to));
    window.scrollTo({ top: 0 });
  }, [at]);
  const next = useCallback(() => go(at + 1), [go, at]);
  // A single choice moves on by itself, after a beat so the tick is seen.
  const nextSoon = () => { clearTimeout(advancing.current); advancing.current = setTimeout(() => go(at + 1), 320); };

  const problems = (ids) => {
    const out = [];
    for (const id of ids) {
      const it = items[id];
      if (it?.required && !filled(s[id])) out.push(it.q.replace(/^Your /, 'your '));
      else if (it?.type === 'tel' && filled(s[id]) && digits(s[id]).length < 10) out.push('a 10 digit WhatsApp number');
    }
    return out;
  };
  const contactIds = isBroker ? ['broker', 'brokerPhone', 'name'] : ['name', 'phone'];

  const reset = () => {
    const kept = Object.fromEntries(KEEP[form].map(k => [k, s[k]]).filter(([, v]) => v));
    setSentBy(isBroker ? s.broker || '' : s.name || '');
    setSentRef(ref);
    setRef(String(1000 + Math.floor(Math.random() * 9000)));
    setS({ kind: s.kind, ...kept });
    setIdx(0);
    started.current = Date.now();
  };

  const whatsappText = () => {
    const head = isBroker
      ? [`*Client requirement from ${s.broker?.trim()}*`, `Broker WhatsApp: ${s.brokerPhone?.trim()}`, `Client: ${s.name?.trim()}`]
      : [`*New requirement from ${s.name?.trim()}*`, `WhatsApp: ${s.phone?.trim()}`];
    return [...head, `Ref: ${ref}`, '', ...answerLines(items, s).map(([q, a]) => `*${q}:* ${a}`)].join('\n');
  };

  const checkContact = () => {
    const bad = problems(contactIds);
    if (bad.length) { setError(`Please add ${bad.join(' and ')}.`); return false; }
    return true;
  };

  const sendWhatsApp = () => {
    if (!checkContact()) return;
    window.open(`https://wa.me/${BROKER_WHATSAPP}?text=${encodeURIComponent(whatsappText())}`, '_blank');
    reset();
    setStatus('whatsapp');
  };

  const send = async () => {
    if (status === 'sending' || !checkContact()) return;
    setError('');
    setStatus('sending');
    try {
      const { _detail, ...answers } = s;
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: PUBLIC_KEY },
        body: JSON.stringify({
          slug: SITE_SLUG, form, kind: s.kind, hp: trap, ms: Date.now() - started.current,
          contact: { name: s.name, phone: s.phone, broker: s.broker, brokerPhone: s.brokerPhone },
          fields: { ...answers, areas: areasOf(s) },
          lines: [['Reference', `No. ${ref}`], ...answerLines(items, s)],
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      reset();
      setStatus('sent');
    } catch {
      setStatus('idle');
      setError('That did not go through. Send it on WhatsApp instead, it reaches us the same way.');
    }
  };

  // Enter moves on; letters pick options on a keyboard.
  const item = typeof screen === 'string' ? items[screen] : null;
  // Set while rendering each screen: what Enter does, and what a letter picks.
  const pick = useRef(null);
  const enter = useRef(null);
  const optionCount = useRef(0);
  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') { e.preventDefault(); enter.current?.(); return; }
      const i = /^[a-z]$/i.test(e.key) ? LETTERS.indexOf(e.key.toUpperCase()) : -1;
      if (i >= 0 && i < optionCount.current) pick.current?.(i);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (status === 'sent' || status === 'whatsapp') {
    const hello = `Hi, this is ${sentBy}. I just sent my requirement on your website.`;
    return (
      <Shell no={sentRef}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          {status === 'sent' && <Stamp no={sentRef}/>}
          <h1 style={titleStyle}>{status === 'sent' ? 'Received. Thank you.' : 'Almost there.'}</h1>
          <p style={{ ...hintStyle, maxWidth: 460 }}>
            {status === 'sent'
              ? (isBroker ? 'I will start searching for this client and come back to you.' : 'Expect a call or a WhatsApp from us within 2 hours.')
              : 'WhatsApp has your answers ready. Tap Send there to finish.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 18, marginTop: 36 }}>
            {status === 'sent' && !isBroker && (
              <a href={`https://wa.me/${BROKER_WHATSAPP}?text=${encodeURIComponent(hello)}`} target="_blank" rel="noreferrer" style={{ color: ACCENT, fontSize: 15, display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                Say hello on WhatsApp <ArrowRight size={15}/>
              </a>
            )}
            {isBroker && <TextLink color={TEXT} onClick={() => setStatus('idle')}>Share another client</TextLink>}
            <a href="/" style={{ color: MUTED, fontSize: 14 }}>Back to brokerinasuit.com</a>
          </div>
        </motion.div>
      </Shell>
    );
  }

  let body;
  pick.current = null;
  enter.current = null;
  optionCount.current = 0;

  if (Array.isArray(screen)) {
    // Contact details: the only screen with several fields.
    const fields = screen.map(id => items[id]);
    const action = isBroker ? () => { if (checkContact()) next(); } : send;
    enter.current = action;
    body = (
      <>
        <h1 style={titleStyle}>{isBroker ? 'Who is sharing this?' : 'Last thing. Where can we reach you?'}</h1>
        <p style={hintStyle}>{isBroker ? 'Saved on this phone, so next time you can skip it.' : 'We reply on WhatsApp within 2 hours.'}</p>
        <div style={{ display: 'grid', gap: 26, marginTop: 30 }}>
          {fields.map((f, i) => (
            <label key={f.id} style={{ display: 'block' }}>
              <span style={{ fontSize: 14, color: MUTED }}>{f.q}</span>
              <BigInput label={f.q} value={s[f.id]} onChange={v => set(f.id, v)} placeholder={f.ph} type={f.type === 'tel' ? 'tel' : 'name'} autoFocus={i === 0} onEnter={action}/>
            </label>
          ))}
        </div>
        {error && <p style={errorStyle}>{error}</p>}
        {isBroker ? <OkButton onClick={action}>Continue</OkButton> : (
          <>
            <OkButton onClick={send} disabled={status === 'sending'}>{status === 'sending' ? 'Sending…' : 'Send my brief'}</OkButton>
            <div style={{ marginTop: 18 }}><TextLink onClick={sendWhatsApp}>Or send it on WhatsApp</TextLink></div>
          </>
        )}
      </>
    );
  } else if (screen === 'gate') {
    const choose = (more) => { setMany({ _detail: more }); setDir(1); setIdx(at + 1); window.scrollTo({ top: 0 }); };
    enter.current = () => choose(false);
    pick.current = (i) => choose(i === 0);
    optionCount.current = 2;
    body = (
      <>
        <h1 style={titleStyle}>That covers the essentials.</h1>
        <p style={hintStyle}>A few more details, like must-haves and deal breakers, help {isBroker ? 'me' : 'us'} shortlist faster. About a minute more.</p>
        <div style={{ display: 'grid', marginTop: 28, maxWidth: 500, borderTop: `1px solid ${LINE}` }}>
          <Option letter="A" label="Add more detail" on={s._detail === true} onClick={() => choose(true)}/>
          <Option letter="B" label={isBroker ? 'Skip, review and send' : 'Skip to the last step'} on={s._detail === false} onClick={() => choose(false)}/>
        </div>
      </>
    );
  } else if (screen === 'send') {
    enter.current = send;
    body = (
      <>
        <h1 style={titleStyle}>Send {s.name?.trim() ? `${s.name.trim()}'s` : 'this'} requirement?</h1>
        <p style={hintStyle}>It reaches me directly. Sending as {s.broker || 'you'}, {s.brokerPhone || ''}. <TextLink onClick={() => go(0)}>Change</TextLink></p>
        {error && <p style={errorStyle}>{error}</p>}
        <OkButton onClick={send} disabled={status === 'sending'}>{status === 'sending' ? 'Sending…' : 'Send requirement'}</OkButton>
        <div style={{ marginTop: 18 }}><TextLink onClick={sendWhatsApp}>Or send it on WhatsApp</TextLink></div>
      </>
    );
  } else if (item) {
    const t = item.type;
    let input = null;
    if (t === 'kind' || t === 'single') {
      const vals = t === 'kind' ? Object.keys(KINDS) : item.options;
      const labels = t === 'kind' ? Object.values(KINDS) : item.options;
      const current = t === 'kind' ? s.kind : s[item.id];
      const choose = (i) => { const v = vals[i]; set(item.id, t === 'single' && current === v ? '' : v); if (current !== v || t === 'kind') nextSoon(); };
      pick.current = choose;
      optionCount.current = labels.length;
      enter.current = next;
      input = (
        <div style={{ display: 'grid', marginTop: 28, maxWidth: 500, borderTop: `1px solid ${LINE}` }}>
          {labels.map((label, i) => <Option key={label} letter={LETTERS[i]} label={label} on={current === vals[i]} onClick={() => choose(i)}/>)}
        </div>
      );
    } else if (t === 'multi') {
      const cur = s[item.id] || [];
      const toggle = (i) => { const v = item.options[i]; set(item.id, cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]); };
      pick.current = toggle;
      optionCount.current = item.options.length;
      enter.current = next;
      input = (
        <>
          <p style={{ ...hintStyle, fontSize: 13, marginTop: item.hint ? 6 : 12 }}>Choose as many as you like</p>
          <div style={{ display: 'grid', marginTop: 16, maxWidth: 500, borderTop: `1px solid ${LINE}` }}>
            {item.options.map((o, i) => <Option key={o} letter={LETTERS[i]} label={o} on={cur.includes(o)} onClick={() => toggle(i)}/>)}
          </div>
          <OkButton onClick={next}>{cur.length ? 'OK' : 'Skip'}</OkButton>
        </>
      );
    } else if (t === 'places') {
      const cur = s.areaPicks || [];
      const toggle = (a) => set('areaPicks', cur.includes(a) ? cur.filter(x => x !== a) : [...cur, a]);
      enter.current = next;
      input = (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', columnGap: 28, marginTop: 24, borderTop: `1px solid ${LINE}` }}>
            {PUNE_AREAS.map(a => <Option key={a} label={a} on={cur.includes(a)} onClick={() => toggle(a)}/>)}
          </div>
          <div style={{ marginTop: 22 }}>
            <BigInput label="Other areas" value={s.areas} onChange={v => set('areas', v)} placeholder="Somewhere else? Type it here" onEnter={next}/>
          </div>
          <OkButton onClick={next}>{cur.length || filled(s.areas) ? 'OK' : 'Skip'}</OkButton>
        </>
      );
    } else if (t === 'budget') {
      const [minId, maxId] = item.fields.map(f => f.id);
      const onIdx = item.ranges.findIndex(([, lo, hi]) => (s[minId] || '') === lo && (s[maxId] || '') === hi);
      const choose = (i) => {
        const [, lo, hi] = item.ranges[i];
        if (onIdx === i) { setMany({ [minId]: '', [maxId]: '' }); return; }
        setMany({ [minId]: lo, [maxId]: hi });
        nextSoon();
      };
      pick.current = exact ? null : choose;
      optionCount.current = exact ? 0 : item.ranges.length;
      enter.current = next;
      input = exact ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 30, maxWidth: 480 }}>
            {item.fields.map((f, i) => (
              <label key={f.id}><span style={{ fontSize: 14, color: MUTED }}>{f.label}</span>
                <BigInput label={f.label} value={s[f.id]} onChange={v => set(f.id, v)} placeholder={f.ph} autoFocus={i === 0} onEnter={next}/></label>
            ))}
          </div>
          <OkButton onClick={next}/>
          <div style={{ marginTop: 16 }}><TextLink onClick={() => setExact(false)}>Pick a range instead</TextLink></div>
        </>
      ) : (
        <>
          <div style={{ display: 'grid', marginTop: 28, maxWidth: 500, borderTop: `1px solid ${LINE}` }}>
            {item.ranges.map(([label], i) => <Option key={label} letter={LETTERS[i]} label={`₹ ${label}`} on={onIdx === i} onClick={() => choose(i)}/>)}
          </div>
          <div style={{ marginTop: 18 }}><TextLink onClick={() => setExact(true)}>Type an exact amount</TextLink></div>
        </>
      );
    } else if (t === 'pair') {
      enter.current = next;
      input = (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 30, maxWidth: 480 }}>
            {item.fields.map((f, i) => (
              <label key={f.id}><span style={{ fontSize: 14, color: MUTED }}>{f.label}</span>
                <BigInput label={f.label} value={s[f.id]} onChange={v => set(f.id, v)} placeholder={f.ph} autoFocus={i === 0} onEnter={next}/></label>
            ))}
          </div>
          <OkButton onClick={next}>{item.fields.some(f => filled(s[f.id])) ? 'OK' : 'Skip'}</OkButton>
        </>
      );
    } else {
      enter.current = next;
      input = (
        <>
          <div style={{ marginTop: 28 }}>
            <BigInput label={item.q} area={t === 'area'} value={s[item.id]} onChange={v => set(item.id, v)} placeholder={item.ph || 'Type your answer'} autoFocus onEnter={next}/>
          </div>
          {t === 'area' && <p style={{ fontSize: 12, color: MUTED, marginTop: 8 }} className="ok-hint">Shift + Enter for a new line</p>}
          <OkButton onClick={next}>{filled(s[item.id]) ? 'OK' : 'Skip'}</OkButton>
        </>
      );
    }
    body = (
      <>
        <h1 style={titleStyle}>
          <span style={{ display: 'block', color: ACCENT, fontSize: 13, marginBottom: 14, fontFamily: MONO, letterSpacing: '0.04em' }}>{String(questionNumber).padStart(2, '0')}.</span>
          {item.q}
        </h1>
        {item.hint && <p style={hintStyle}>{item.hint}</p>}
        {input}
      </>
    );
  }

  return (
    <Shell no={ref} onBack={at > 0 ? () => go(at - 1) : null}>
      <AnimatePresence mode="wait" custom={dir} initial={false}>
        <motion.div key={`${at}-${typeof screen === 'string' ? screen : 'contact'}`}
          custom={dir}
          initial={{ opacity: 0, y: dir * 36 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: dir * -36 }}
          transition={{ duration: 0.38, ease: EASE }}
        >
          {at === 0 && (
            <p style={{ fontSize: 15, color: MUTED, marginBottom: 22 }}>
              {isBroker ? 'Share a client requirement. Takes about a minute.' : 'Your property brief. Takes about a minute.'}
            </p>
          )}
          {body}
        </motion.div>
      </AnimatePresence>
      {/* Hidden from people; bots that fill every field give themselves away. */}
      <input value={trap} onChange={e => setTrap(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" name="website"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}/>
    </Shell>
  );
}

function Stamp({ no }) {
  const date = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.6, rotate: -14 }} animate={{ opacity: 0.88, scale: 1, rotate: -7 }}
      transition={{ delay: 0.15, duration: 0.22, ease: [0.5, 0, 0.75, 0] }}
      style={{
        display: 'inline-block', marginBottom: 34, padding: '10px 18px', color: ACCENT,
        border: `2.5px solid ${ACCENT}`, outline: `1px solid ${ACCENT}`, outlineOffset: 3,
        fontFamily: MONO, textAlign: 'center', mixBlendMode: 'multiply',
      }}>
      <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: '0.18em' }}>RECEIVED</div>
      <div style={{ fontSize: 11, letterSpacing: '0.12em', marginTop: 2 }}>No. {no} · {date}</div>
    </motion.div>
  );
}

function Shell({ children, onBack, no, count }) {
  return (
    <div style={{ minHeight: '100svh', background: PAPER, color: INK, position: 'relative' }}>
      <style>{G}{`
        body { cursor: auto; background: ${PAPER}; color: ${INK}; }
        ::selection { background: rgba(158,58,43,0.18); color: ${INK}; }
        textarea::placeholder, input::placeholder { color: rgba(28,26,22,0.3); }
        @media (pointer: coarse) { .ok-hint { display: none; } }
      `}</style>
      {/* An @import after the site's styles would be ignored, so the typewriter face loads here. */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap"/>
      {/* Paper grain, so the page reads as stock rather than a flat fill. */}
      <div aria-hidden="true" style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', opacity: 0.5, mixBlendMode: 'multiply',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.28 0 0 0 0.09 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }}/>
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40, background: PAPER }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px 14px', maxWidth: 1100, margin: '0 auto' }}>
          <a href="/" aria-label="Broker in a Suit home"><img src="/logo-white.svg" alt="Broker in a Suit" style={{ height: 17, display: 'block', filter: 'invert(1)' }}/></a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontFamily: MONO, fontSize: 12, color: MUTED }}>
            {count && <span>{count}</span>}
            {no && <span>No. {no}</span>}
          </div>
        </div>
        {/* Perforation, like the tear-off edge of a ticket. */}
        <div style={{ height: 0, borderBottom: `1.5px dashed ${LINE}` }}/>
      </header>
      <main style={{ minHeight: '100svh', display: 'flex', alignItems: 'center', padding: '104px 24px 110px', position: 'relative' }}>
        <div style={{ width: '100%', maxWidth: 620, margin: '0 auto' }}>{children}</div>
      </main>
      {onBack && (
        <button type="button" onClick={onBack} style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40, display: 'flex', alignItems: 'center', gap: 8,
          background: PAPER, border: 'none', borderTop: `1px solid ${LINE}`, color: MUTED,
          fontFamily: MONO, fontSize: 13, cursor: 'pointer', padding: '14px 24px',
        }}><ArrowLeft size={15}/> Previous question</button>
      )}
    </div>
  );
}

const titleStyle = {
  fontFamily: 'Playfair Display, serif', fontWeight: 400, fontSize: 'clamp(28px, 5.4vw, 42px)',
  lineHeight: 1.18, color: TEXT, letterSpacing: '-0.01em',
};
const hintStyle = { fontSize: 16, color: MUTED, marginTop: 12, lineHeight: 1.6 };
const errorStyle = { color: ACCENT, fontSize: 14, marginTop: 18 };
